import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import TitleHeader from '../common/TitleHeader';
import useAppAuthStore from '@/shared/store/UseAppAuthStore';
import { getUploadsUrl, vAppAjax } from '@/shared/utils/CommonUtil';
import logger from '@/shared/utils/Logger';
import characterBg from '@/assets/app/images/mypage/chracter_bg.png';
import AppButton from '@/shared/components/ui/new/AppButton';
import { useNavigate } from 'react-router';

interface Character {
  characterId: number;
  characterName: string;
  provisionTypeCode: string;
  imageFilename: string | null;
  originalFilename: string | null;
  description: string | null;
  price: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  thumbnailFilename: string | null;
  thumbnailOrgFilename: string | null;
  descImageFilename: string | null;
  descOrgFilename: string | null;
}

interface CharacterSelectRequest {
  characterId: number;
}

const ITEM_WIDTH = 80; // 아이템 너비
const GAP = 16; // 아이템 간격
const STEP = ITEM_WIDTH + GAP;

const CharacterChange: React.FC = () => {
  const { member, checkAttendance } = useAppAuthStore();
  const [loading, setLoading] = useState<boolean>(true);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(2);
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<number | undefined>(
    member?.selectedCharacterId || 14,
  );

  // 캐릭터 목록 조회
  useEffect(() => {
    loadCharacters();
  }, []);

  const loadCharacters = async () => {
    setLoading(true);
    await vAppAjax(
      'get',
      `characters`,
      {},
      (response: AxiosResponse<CommonResult<Character[]>>) => {
        const resultData = response.data;
        setLoading(false);
        if (resultData.code === 200) {
          setCharacters(resultData.result);
          resultData.result.forEach((item, index) => {
            if (item.characterId === member?.selectedCharacterId) {
              setSelectedIndex(index);
            }
          });
        } else {
          logger.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  const handleSelectCharacter = async () => {
    const selectedId = characters[selectedIndex].characterId;
    if (member?.selectedCharacterId === selectedId) return;

    await vAppAjax(
      'post',
      `characters/select`,
      { characterId: selectedId },
      (response: AxiosResponse<CommonResult<CharacterSelectRequest>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          checkAttendance();
        } else {
          logger.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  // 드래그가 끝났을 때 어느 칸에 멈출지 계산하는 함수
  const handleDragEnd = (_: any, info: any) => {
    const dragOffset = info.offset.x;
    const dragThreshold = 50; // 50px 이상 밀었을 때 다음 칸으로 이동

    if (dragOffset < -dragThreshold && selectedIndex < characters.length - 1) {
      setSelectedIndex(prev => prev + 1);
    } else if (dragOffset > dragThreshold && selectedIndex > 0) {
      setSelectedIndex(prev => prev - 1);
    }
  };

  if (loading) {
    return <></>;
  }

  return (
    <div className="flex flex-col h-screen bg-white overflow-hidden">
      <TitleHeader title="캐릭터변경" onBack={() => navigate(-1)} />

      <main className="flex-1 px-4 items-start justify-center">
        <div
          className="w-full relative rounded-[20px] overflow-hidden"
          style={{
            backgroundImage: `url(${characterBg})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            aspectRatio: '310 / 448',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={characters[selectedIndex].characterId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`w-full aspect-[4/5] bg-gradient-to-b ${characters[selectedIndex]} rounded-[40px] p-8 relative flex flex-col items-center `}
            >
              <h2
                className="text-white mb-6 text-outline-blue"
                style={{ fontSize: 20, fontFamily: 'ONE-Mobile-POP' }}
              >
                {characters[selectedIndex].characterName}
              </h2>

              <div
                className="flex items-center justify-center absolute"
                style={{ top: '32%', flexDirection: 'column', width: '80%' }}
              >
                <img
                  src={`${getUploadsUrl()}/character/${characters[selectedIndex].imageFilename}`}
                  style={{ width: 'auto', height: 200 }}
                  alt="char"
                />
              </div>
            </motion.div>
            <div className="text-center absolute" style={{ bottom: '4%' }}>
              {characters[selectedIndex].descImageFilename && (
                <img
                  src={`${getUploadsUrl()}/character/${characters[selectedIndex].descImageFilename}`}
                />
              )}
            </div>
          </AnimatePresence>
        </div>

        <div className="w-full flex justify-center items-center mt-3 mb-5">
          {/* 전체 배경 베이스 트랙 (연한 블루) */}
          <div className="w-100 h-2 bg-[#E2F3FF] rounded-[20px] relative overflow-hidden">
            {/* 현재 선택된 위치를 나타내는 활성화 바 (진한 블루) */}
            <motion.div
              className="h-full bg-[#38B6FF] rounded-[20px] absolute top-0 left-0"
              style={{
                // 캐릭터 전체 개수에 맞춰 활성화 바의 너비를 균등 분할
                width: `${100 / (characters.length || 1)}%`,
              }}
              animate={{
                // 현재 선택된 인덱스만큼 x축으로 정밀 이동
                x: `${selectedIndex * 100}%`,
              }}
              transition={{ type: 'spring', stiffness: 250, damping: 28 }}
            />
          </div>
        </div>

        {/* 하단 슬라이더 영역 */}
        <div className="w-full overflow-visible flex items-center justify-center relative mt-4 h-32">
          <motion.div
            drag="x"
            dragConstraints={{ left: 0, right: 0 }} // 한 칸씩 제어하기 위해 축 고정
            onDragEnd={handleDragEnd}
            animate={{
              // 핵심: 현재 인덱스만큼 왼쪽으로 밀기
              x: -((selectedIndex - 2) * STEP),
            }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="flex items-center cursor-grab active:cursor-grabbing"
            style={{
              // 트랙을 중앙에 배치하기 위해 width를 0으로 만들거나 최소화
              width: 0,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {characters.map((char, idx) => {
              const isActive = idx === selectedIndex;
              return (
                <motion.div
                  key={char.characterId}
                  onClick={() => setSelectedIndex(idx)}
                  animate={{
                    scale: isActive ? 1.15 : 1.05,
                    opacity: isActive ? 1 : 0.6,
                  }}
                  className={`relative w-[100px] h-[100px] shrink-0 rounded-full flex items-center justify-center`}
                  style={{
                    // 중앙 배치를 위해 양옆 마진을 동일하게 부여 (STEP 유지)
                    marginLeft: GAP / 2,
                    marginRight: GAP / 2,
                    transformOrigin: 'bottom center',
                  }}
                >
                  <img
                    src={`${getUploadsUrl()}/character/${characters[idx].thumbnailFilename}`}
                    className="rounded-full w-full h-full object-contain p-1"
                  />
                  {isActive && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-0 left-0 bg-[#53C6FF] p-1 rounded-full border-2 border-white z-20"
                    >
                      <Check className="w-4 h-4 text-white stroke-[z]" />
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        <AppButton
          onConfirm={handleSelectCharacter}
          style={{ marginTop: 10, marginBottom: 30, fontSize: 28 }}
          className="f-one-pop"
          message={'적용하기'}
        />
      </main>
    </div>
  );
};

export default CharacterChange;
